import * as fs from "node:fs/promises";
import * as path from "node:path";
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";

export interface Profile {
  name: string;
  savedAt: string;
  subscriptionId: string;
  tenantId: string;
  config: { tenantId: string; clientId: string; projectEndpoint: string };
  seedEndpoint: string;
}

function profilesDir(): string {
  const home = process.env.HOME ?? process.env.USERPROFILE ?? "";
  return path.join(home, ".m8t", "profiles");
}

export function getProfilePath(name: string): string {
  return path.join(profilesDir(), `${path.basename(name)}.yaml`);
}

/** Friendly slug from a tenant domain (or id): lowercase, prefix before the
 *  first dot, alnum+dash only. Falls back to "profile". */
export function slugifyTenant(domainOrId: string): string {
  const base = domainOrId.split(".")[0].toLowerCase();
  const slug = base.replace(/[^a-z0-9-]/g, "");
  return slug.length > 0 ? slug : "profile";
}

export async function listProfiles(): Promise<string[]> {
  try {
    const entries = await fs.readdir(profilesDir());
    return entries
      .filter((e) => e.endsWith(".yaml"))
      .map((e) => e.replace(/\.yaml$/, ""))
      .sort();
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
    throw e;
  }
}

export async function readProfile(name: string): Promise<Profile | null> {
  try {
    const raw = await fs.readFile(getProfilePath(name), "utf8");
    const parsed: unknown = parseYaml(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as Profile;
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return null;
    throw e;
  }
}

async function exists(p: string): Promise<boolean> {
  try {
    await fs.stat(p);
    return true;
  } catch {
    return false;
  }
}

/** Save a profile. If `name` is taken, append -2, -3, … Returns the saved name. */
export async function saveProfile(p: Profile): Promise<string> {
  await fs.mkdir(profilesDir(), { recursive: true });
  let name = p.name;
  let n = 2;
  while (await exists(getProfilePath(name))) {
    name = `${p.name}-${n.toString()}`;
    n++;
  }
  const target = getProfilePath(name);
  const tmp = `${target}.tmp`;
  await fs.writeFile(tmp, stringifyYaml({ ...p, name }, { indent: 2 }), "utf8");
  await fs.rename(tmp, target);
  return name;
}
